import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Button, Paper, Typography, Avatar, CircularProgress } from '@mui/material';
import { Send, ArrowBack } from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../utils/api';
import { getUserId } from '../../utils/api';
import Header from '../Header';
import './TicketChat.css';

const TicketChat = ({ ticketId, ticketName, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);
  const [attachedFiles, setAttachedFiles] = useState([]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadMessages();
  }, [ticketId]);

  const loadMessages = async () => {
    try {
      setIsLoading(true);
      const response = await api.post('/tickets/singlechat', { "id": ticketId })
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e) => {
    setAttachedFiles(Array.from(e.target.files));
  };

  const handleRemoveFile = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Helper to read a file as base64
  const readFileAsBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const sendMessage = async () => {
    if (!newMessage.trim() && attachedFiles.length === 0) return;

    try {
      setIsSending(true);
      // If there are files, send one message per file (with note)
      if (attachedFiles.length > 0) {
        for (let i = 0; i < attachedFiles.length; i++) {
          const file = attachedFiles[i];
          const base64 = await readFileAsBase64(file);
          const payload = {
            subject: 'Ticket Chat Attachment',
            notetext: newMessage.trim(),
            filename: file.name,
            mimetype: file.type,
            documentbody: base64,
            id: ticketId, // Pass ticketId, let backend map to objectid_incident@odata.bind
            isdocument: true,
          };
          await api.post('/tickets/sendmessage', payload);
        }
      } else {
        // Only note, no files
        const payload = {
          subject: 'Ticket Chat Note',
          notetext: newMessage.trim(),
          id: ticketId,
        };
        await api.post('/tickets/sendmessage', payload);
      }
      setNewMessage('');
      setAttachedFiles([]);
      loadMessages();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOwnMessage = (senderId) => {
    return senderId === getUserId();
  };

  const renderMessageContent = (message) => {
    if (message.isdocument && message.filename && message.documentbody) {
      return (
        <>
          <Typography variant="body1" className="message-text">
            Attachment: {message.filename}
          </Typography>
          <a
            href={`data:${message.mimetype};base64,${message.documentbody}`}
            download={message.filename}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download
          </a>
          {message.notetext && (
            <Typography variant="body2" className="message-text">
              {message.notetext}
            </Typography>
          )}
        </>
      );
    }
    return (
      <Typography variant="body1" className="message-text">
        {message.notetext || message.message}
      </Typography>
    );
  };

  // Helper to get date group label
  const getDateGroupLabel = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isToday = date.toDateString() === today.toDateString();
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';
    return date.toLocaleDateString();
  };

  return (
    <>
      <Box className="chat-container">
        <Paper className="chat-header" style={{ display: 'flex', alignItems: 'left', justifyContent: 'flex-start', position: 'relative' }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            variant="text"
            className="back-button"
            style={{ marginRight: 16 }}
          >
            Back to Tickets
          </Button>
          <Typography variant="h6" className="ticket-title">
            Chat - {ticketName}
          </Typography>
        </Paper>

        <Paper className="chat-messages">
          {isLoading ? (
            <Box className="loading-container">
              <CircularProgress />
              <Typography>Loading messages...</Typography>
            </Box>
          ) : messages.length === 0 ? (
            <Box className="no-messages">
              <Typography variant="body1" color="textSecondary">
                No messages yet. Start the conversation!
              </Typography>
            </Box>
          ) : (
            <Box className="messages-list">
              {(() => {
                let lastDateLabel = null;
                return messages.map((message, index) => {
                  const isMe = message._createdby_value === "5bb34e57-49d2-e911-81b5-b8ac6f4102d0";
                  const dateValue = message.timestamp || message.createdon;
                  const dateLabel = getDateGroupLabel(dateValue);
                  const showDateLabel = dateLabel !== lastDateLabel;
                  lastDateLabel = dateLabel;
                  return (
                    <React.Fragment key={index}>
                      {showDateLabel && (
                        <Box className="date-group-label" style={{ textAlign: 'center', margin: '16px 0', color: '#888' }}>
                          <Typography variant="caption">{dateLabel}</Typography>
                        </Box>
                      )}
                      <Box
                        className={`message ${isMe ? 'own-message' : 'other-message'}`}
                        style={{ display: 'flex', flexDirection: isMe ? 'row-reverse' : 'row', alignItems: 'center', justifyContent: isMe ? 'flex-end' : 'flex-start' }}
                      >
                        <Box className="message-content">
                          {renderMessageContent(message)}
                          <Typography variant="caption" className="message-time">
                            {formatTime(dateValue)}
                          </Typography>
                        </Box>
                        <Avatar className="message-avatar">
                          {isMe ? "Me" : "IT"}
                        </Avatar>
                      </Box>
                    </React.Fragment>
                  );
                });
              })()}
              <div ref={messagesEndRef} />
            </Box>
          )}
          {/* Refresh icon inside conversation, right-aligned */}
          <Box style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', margin: '8px 0 0 0' }}>
            <Button onClick={loadMessages} variant="text" style={{ minWidth: 0, padding: 6 }} title="Refresh conversation">
              <RefreshIcon />
            </Button>
          </Box>
        </Paper>

        <Paper className="chat-input">
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            variant="outlined"
            disabled={isSending}
            className="message-input"
          />
          <input
            type="file"
            multiple
            onChange={handleFileChange}
            disabled={isSending}
            style={{ margin: '8px 0' }}
          />
          {attachedFiles.length > 0 && (
            <Box className="attached-files-list" sx={{ mt: 1, mb: 1 }}>
              {attachedFiles.map((file, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>{file.name}</Typography>
                  <Button size="small" color="error" onClick={() => handleRemoveFile(idx)} disabled={isSending}>
                    Remove
                  </Button>
                </Box>
              ))}
            </Box>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={sendMessage}
            disabled={(!newMessage.trim() && attachedFiles.length === 0) || isSending}
            endIcon={<Send />}
            className="send-button"
          >
            Send
          </Button>
        </Paper>
      </Box>
    </>
  );
};

export default TicketChat;